import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormInputText, InputLabel, Text } from '@hopskipdrive/react-components';

import { useAuthQuery } from '../../../../auth/hooks/useAuthQuery';
import { useFlags } from '../../../../contexts/LDContext';
import { GroupedOption } from '../../../../hooks/useLocationOptions';
import { SelectAccountLocation } from '../../../../organizers/components/SelectAccountLocation';
import { getSavedAccountLocationId } from '../../../../utils/helpers/storage';

import { FieldWrapper } from './ShareAccessForm.styles';

type FormData = {
  emails: string;
  defaultAccountLocation: GroupedOption | null;
  accountLocations: GroupedOption[] | null;
};

interface ShareAccessFormProps {
  handleSubmit: (data) => void;
}

export const ShareAccessForm: FC<ShareAccessFormProps> = ({ handleSubmit }) => {
  const methods = useFormContext<FormData>();
  const {
    control,
    formState: { errors },
  } = methods;
  const { enableDailyQueueAssignedLocations } = useFlags();
  const { user } = useAuthQuery();

  // Only trust the saved id if it's still one of this user's own account locations.
  const savedAccountLocationId = getSavedAccountLocationId();
  const currentSelectedLocation = user?.accountLocations?.find(
    (location) => savedAccountLocationId && String(location.id) === savedAccountLocationId
  );

  return (
    <form id="share-access-form" onSubmit={methods.handleSubmit(handleSubmit)}>
      <FieldWrapper>
        <InputLabel fieldtype="required" htmlFor="emails">
          Email addresses
        </InputLabel>
        <Controller
          control={control}
          name="emails"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <FormInputText
              variant="outlined"
              id="emails"
              name="emails"
              value={value}
              placeholder="Enter email addresses separated by commas: example1@example1.com, example2@example2.com"
              multiline
              fullWidth
              rows={4}
              data-testid="email-addresses"
              error={!!errors.emails}
              helperText={errors.emails?.message}
              onChange={onChange}
            />
          )}
        />
      </FieldWrapper>
      <FieldWrapper>
        <InputLabel
          id={enableDailyQueueAssignedLocations ? 'accountLocations-label' : 'defaultAccountLocation-label'}
          fieldtype="required"
          htmlFor={enableDailyQueueAssignedLocations ? 'assigned-locations' : 'default-location'}
        >
          {enableDailyQueueAssignedLocations ? 'Locations' : 'Location'}
        </InputLabel>
        <Controller
          control={control}
          name={enableDailyQueueAssignedLocations ? 'accountLocations' : 'defaultAccountLocation'}
          render={({ field: { onChange } }) => (
            <SelectAccountLocation
              onChange={onChange}
              defaultValue={
                enableDailyQueueAssignedLocations && currentSelectedLocation
                  ? [currentSelectedLocation]
                  : currentSelectedLocation
              }
              multiple={enableDailyQueueAssignedLocations}
              placeholder={enableDailyQueueAssignedLocations ? 'Select locations' : 'Select location'}
              error={enableDailyQueueAssignedLocations ? !!errors.accountLocations : !!errors.defaultAccountLocation}
              helperText={
                enableDailyQueueAssignedLocations
                  ? (errors.accountLocations?.message as string)
                  : (errors.defaultAccountLocation?.message as string)
              }
            />
          )}
        />
      </FieldWrapper>
      <FieldWrapper>
        <InputLabel>Role</InputLabel>
        <Text>Onsite Staff - View Only</Text>
      </FieldWrapper>
    </form>
  );
};
